import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { runPrompt, deleteRun, getRunById, getProject } from '../../services/api';
import { getModelDisplayName, DEFAULT_MODEL } from '../../constants/models';
import { buildLLMConfig } from '../../constants/llmConfig';
import ModelSelector from '../PromptWizard/ModelSelector';
import { highlightMarkdown } from '../common/MarkdownHighlighter';
import { useModal } from '../../contexts/ModalContext';
import { useWorkspace } from '../../contexts/WorkspaceContext';

const ArrayInput = ({ value, onChange, disabled, placeholder }) => {
  const items = Array.isArray(value) ? value : [];

  const handleAddItem = () => {
    onChange([...items, '']);
  };

  const handleRemoveItem = (index) => {
    const newItems = items.filter((_, i) => i !== index);
    onChange(newItems);
  };

  const handleItemChange = (index, newValue) => {
    const newItems = [...items];
    newItems[index] = newValue;
    onChange(newItems);
  };

  return (
    <div className="space-y-2">
      {items.map((item, index) => (
        <div key={index} className="flex items-center gap-2">
          <span className="w-6 h-6 flex items-center justify-center text-xs font-medium text-text-secondary bg-bg-tertiary rounded border border-border-primary">{index + 1}</span>
          <input
            type="text"
            className="flex-1 px-4 py-2.5 text-sm text-text-primary bg-bg-elevated border border-primary-700/50 rounded-lg focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 transition-all duration-fast placeholder:text-text-tertiary shadow-inner disabled:opacity-50 disabled:cursor-not-allowed"
            value={item}
            onChange={(e) => handleItemChange(index, e.target.value)}
            placeholder={placeholder || `Item ${index + 1}`}
            disabled={disabled}
          />
          <button
            type="button"
            className="w-8 h-8 flex items-center justify-center text-text-tertiary hover:text-error-DEFAULT hover:bg-error-50 rounded-lg transition-colors duration-fast disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={() => handleRemoveItem(index)}
            disabled={disabled}
            title="Remove item"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
      ))}
      <button
        type="button"
        className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-primary-500 hover:text-primary-400 hover:bg-primary-500/10 rounded-lg transition-colors duration-fast disabled:opacity-50 disabled:cursor-not-allowed"
        onClick={handleAddItem}
        disabled={disabled}
      >
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <line x1="12" y1="5" x2="12" y2="19"></line>
          <line x1="5" y1="12" x2="19" y2="12"></line>
        </svg>
        Add Item
      </button>
    </div>
  );
};

ArrayInput.propTypes = {
  value: PropTypes.array,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  placeholder: PropTypes.string,
};

const TestPromptPage = ({ project, onBack, onSelectProject, projects, runs: propsRuns, runsLoading, onRunsUpdate }) => {
  const navigate = useNavigate();
  const { runId } = useParams();
  const { showConfirm } = useModal();
  const { currentWorkspace, getAvailableProviders } = useWorkspace();
  const [inputValues, setInputValues] = useState({});
  
  const availableProviders = getAvailableProviders();
  const [runName, setRunName] = useState('');
  const [isRunning, setIsRunning] = useState(false);
  const [output, setOutput] = useState('');
  const [result, setResult] = useState(null);
  const [activeResultTab, setActiveResultTab] = useState('output');
  const [selectedProject, setSelectedProject] = useState(project);
  const [selectedModel, setSelectedModel] = useState(null);
  const [llmConfig, setLLMConfig] = useState(null);
  const [selectedRun, setSelectedRun] = useState(null);
  const [viewMode, setViewMode] = useState('allRuns');
  const [wizardStep, setWizardStep] = useState(1);
  const [searchProject, setSearchProject] = useState('');
  const [searchRun, setSearchRun] = useState('');
  const [activePromptType, setActivePromptType] = useState('system'); // 'system' or 'user'
  const outputRef = useRef(null);
  
  const runs = useMemo(() => propsRuns || [], [propsRuns]);

  // Filter runs based on search query
  const filteredRuns = useMemo(() => {
    if (!searchRun) return runs;
    const searchLower = searchRun.toLowerCase();
    return runs.filter(run => {
      const runName = run.run_name || '';
      return runName.toLowerCase().includes(searchLower);
    });
  }, [runs, searchRun]);

  const completedProjects = useMemo(() => projects?.filter(p => p.status === 'complete') || [], [projects]);

  const userVariables = useMemo(() => selectedProject?.user_input?.input_variables || [], [selectedProject?.user_input?.input_variables]);
  const additionalVariables = useMemo(() => selectedProject?.user_input?.additional_input_variables || [], [selectedProject?.user_input?.additional_input_variables]);

  useEffect(() => {
    if (project) {
      setSelectedProject(project);
      setViewMode('wizard');
      setWizardStep(2);
    }
  }, [project]);

  useEffect(() => {
    const loadRunFromUrl = async () => {
      if (runId && runId !== 'new') {
        const result = await getRunById(runId);
        if (result.success) {
          setSelectedRun(result.data);
          setViewMode('view');
          setActiveResultTab('input');
        } else {
          toast.error('Failed to load run details');
          navigate('/test');
        }
      }
    };
    
    loadRunFromUrl();
  }, [runId, navigate]);

  useEffect(() => {
    if (wizardStep === 3 && selectedProject) {
      const allVars = [
        ...(selectedProject?.user_input?.input_variables || []),
        ...(selectedProject?.user_input?.additional_input_variables || []),
      ];
      
      setInputValues(prev => {
        const hasExistingValues = Object.keys(prev).length > 0 && 
          allVars.some(v => prev[v.name] !== undefined && prev[v.name] !== '' && 
            (Array.isArray(prev[v.name]) ? prev[v.name].length > 0 : true));
        
        if (hasExistingValues) {
          return prev;
        }
        
        const initial = {};
        allVars.forEach(v => {
          initial[v.name] = v.type === 'Array List' ? [] : '';
        });
        return initial;
      });
    }
  }, [wizardStep, selectedProject]);
  
  useEffect(() => {
    if (wizardStep === 2 && !selectedModel && selectedProject) {
      setSelectedModel(selectedProject?.model || null);
    }
  }, [wizardStep, selectedProject, selectedModel]);

  useEffect(() => {
    if (isRunning && outputRef.current) {
      outputRef.current.scrollTop = outputRef.current.scrollHeight;
    }
  }, [output, isRunning]);

  useEffect(() => {
    if (activeResultTab === 'prompts') {
      if (selectedRun?.system_prompt || result?.system_prompt) {
        setActivePromptType('system');
      } else if (selectedRun?.user_prompt || result?.user_prompt) {
        setActivePromptType('user');
      }
    }
  }, [activeResultTab, selectedRun, result]);

  const handleSelectProjectFromList = async (proj) => {
    if (onSelectProject) {
      await onSelectProject(proj);
    } else {
      setSelectedProject(proj);
    }
    setWizardStep(2);
    
  };

  const handleBackToAllRuns = () => {
    setSelectedProject(null);
    setSelectedRun(null);
    setViewMode('allRuns');
    setOutput('');
    setResult(null);
    setWizardStep(1);
    setSearchProject('');
    navigate('/test');
  };

  const handleNewRun = () => {
    setViewMode('wizard');
    setSelectedProject(null);
    setSelectedRun(null);
    setOutput('');
    setResult(null);
    setWizardStep(1);
    setSearchProject('');
    navigate('/test/new');
  };
  
  const handleNextStep = () => {
    if (wizardStep === 1 && !selectedProject) {
      toast.error('Please select a project');
      return;
    }
    if (wizardStep === 2 && !runName.trim()) {
      toast.error('Please enter a run name');
      return;
    }
    setWizardStep(prev => prev + 1);
  };
  
  const handlePrevStep = () => {
    setWizardStep(prev => Math.max(1, prev - 1));
  };

  const handleViewRun = async (run) => {
    const result = await getRunById(run.id);
    if (result.success) {
      setSelectedRun(result.data);
      setViewMode('view');
      setActiveResultTab('input');
      navigate(`/test-details/${run.id}`);
    } else {
      toast.error('Failed to load run details');
    }
  };

  const handleDeleteRun = async (runId, e) => {
    e.stopPropagation();
    const confirmed = await showConfirm({
      title: 'Delete Run',
      message: 'Delete this run? This cannot be undone.',
      confirmText: 'Delete',
      variant: 'danger'
    });
    if (!confirmed) return;
    
    const response = await deleteRun(runId);
    if (response.success) {
      toast.success('Run deleted');
      onRunsUpdate(prev => prev.filter(run => run.id !== runId));
    } else {
      toast.error('Failed to delete run');
    }
  };

  const handleInputChange = (name, value) => {
    setInputValues(prev => ({ ...prev, [name]: value }));
  };


  const handleRun = useCallback(async () => {
    if (!selectedProject) {
      toast.error('Please select a project first');
      return;
    }

    if (!runName.trim()) {
      toast.warning('Please enter a test run name');
      return;
    }

    const missingVars = userVariables.filter(v => {
      const val = inputValues[v.name];
      if (v.type === 'Array List') {
        return !val || val.length === 0 || val.every(item => !item.trim());
      }
      return !val || val.trim() === '';
    });

    if (missingVars.length > 0) {
      toast.error(`Required fields missing: ${missingVars.map(v => v.name).join(', ')}`);
      return;
    }

    const cleanedInputs = {};
    [...userVariables, ...additionalVariables].forEach(v => {
      const val = inputValues[v.name];
      if (v.type === 'Array List') {
        const filtered = (val || []).filter(item => item.trim());
        if (filtered.length > 0) {
          cleanedInputs[v.name] = filtered;
        }
      } else if (val && val.trim()) {
        cleanedInputs[v.name] = val.trim();
      }
    });

    const tempRun = {
      id: 'streaming',
      run_name: runName.trim(),
      model: selectedModel || selectedProject.model,
      input_values: cleanedInputs,
      system_prompt: '',
      user_prompt: '',
      few_shot_examples: '',
      output: '',
      created_at: new Date().toISOString()
    };
    
    setSelectedRun(tempRun);
    setViewMode('view');
    setActiveResultTab('output');
    setIsRunning(true);
    setOutput('');
    setResult(null);

    const modelToUse = selectedModel || selectedProject.model;
    const configToUse = llmConfig || buildLLMConfig(modelToUse);

    await runPrompt(
      selectedProject.id,
      runName.trim(),
      cleanedInputs,
      configToUse,
      (chunk) => {
        setOutput(prev => prev + chunk);
      },
      (data) => {
        setResult(data);
        setIsRunning(false);
        toast.success('Run saved successfully!');
        
        if (data.run_id) {
          const completedRun = {
            id: data.run_id,
            run_name: runName.trim(),
            model: modelToUse,
            input_values: cleanedInputs,
            system_prompt: data.system_prompt,
            user_prompt: data.user_prompt,
            few_shot_examples: data.few_shot_examples,
            output: data.output,
            created_at: new Date().toISOString(),
            project_name: selectedProject?.project_name
          };
          onRunsUpdate(prev => [completedRun, ...prev]);
          setSelectedRun(completedRun);
          setActiveResultTab('output');
        }
      },
      (error) => {
        setIsRunning(false);
        setViewMode('wizard');
        setWizardStep(3);
        toast.error(error || 'Failed to run prompt');
      },
      currentWorkspace?.id || currentWorkspace?._id
    );
  }, [selectedProject, inputValues, runName, selectedModel, llmConfig, userVariables, additionalVariables, onRunsUpdate, currentWorkspace]);

  const handleCopy = useCallback(async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Copied!');
    } catch {
      toast.error('Failed to copy');
    }
  }, []);

  const handleRunAgain = useCallback(async (run) => {
    const result = await getProject(run.project_id);
    if (result.success) {
      const fullProject = result.data;
      setSelectedProject(fullProject);
      setInputValues(run.input_values || {});
      setRunName(prev => prev || `${run.run_name} (Copy)`);
      setSelectedModel(run.model);
      setViewMode('wizard');
      setWizardStep(2); // Skip project selection, go to run configuration
      navigate('/test/new');
      toast.success('Ready to run again with same inputs');
    } else {
      toast.error('Failed to load prompt details');
    }
  }, [navigate]);

  const renderVariableInput = (variable, isRequired) => (
    <div key={variable.name} className="space-y-2.5">
      <div className="flex items-start justify-between gap-2">
        <span className="text-sm font-medium text-text-primary flex items-center gap-1.5">
          {variable.name}
          {isRequired && <span className="text-error-500">*</span>}
        </span>
        <span className="text-xs font-medium py-0.5 px-2 bg-primary-600 text-white rounded font-mono">{variable.type}</span>
      </div>
      <p className="text-xs text-text-tertiary">{variable.description}</p>
      {variable.type === 'Array List' ? (
        <ArrayInput
          name={variable.name}
          value={inputValues[variable.name] || []}
          onChange={(val) => handleInputChange(variable.name, val)}
          disabled={isRunning}
          placeholder={`Enter ${variable.name} item`}
        />
      ) : (
        <input
          type="text"
          className="w-full px-4 py-2.5 text-sm text-text-primary bg-bg-elevated border border-primary-700/50 rounded-lg focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 transition-all duration-fast placeholder:text-text-tertiary shadow-inner disabled:opacity-50 disabled:cursor-not-allowed"
          value={inputValues[variable.name] || ''}
          onChange={(e) => handleInputChange(variable.name, e.target.value)}
          placeholder={`Enter ${variable.name}...`}
          disabled={isRunning}
        />
      )}
    </div>
  );

  if (viewMode === 'allRuns') {
    if (runsLoading) {
      return (
        <div className="flex-1 flex flex-col items-center justify-center gap-4 p-10 text-center">
          <div className="w-10 h-10 border-2 border-border-primary border-t-primary-500 rounded-full animate-spin"></div>
          <p className="text-text-secondary text-base mt-4">Loading runs...</p>
        </div>
      );
    }

    if (runs.length === 0) {
      return (
        <div className="h-full flex flex-col bg-transparent overflow-hidden">
          <div className="page-header">
            <div className="page-header-content">
              <h2 className="page-title">Test & Preview</h2>
              <p className="page-subtitle">0 runs</p>
            </div>
            <div className="page-actions">
              <button onClick={handleNewRun} className="btn-primary">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polygon points="5 3 19 12 5 21 5 3"/>
                </svg>
                New Run
              </button>
            </div>
          </div>
          
          <div className="flex flex-col items-center justify-center min-h-[500px] text-center p-16 max-w-2xl mx-auto">
            <div className="w-20 h-20 rounded-full bg-bg-tertiary flex items-center justify-center mb-6 border-2 border-border-primary">
              <svg className="w-10 h-10 text-primary-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon>
              </svg>
            </div>
            <h3 className="text-text-primary text-2xl font-semibold m-0 mb-3">No Test Runs Yet</h3>
            <p className="text-text-secondary text-base mb-6 leading-relaxed max-w-md">Start testing your AI prompts by creating your first run. Test runs help you validate and refine your prompt templates with real data.</p>
            <button onClick={handleNewRun} className="px-6 py-3 text-base font-semibold bg-primary-600 text-white rounded-lg cursor-pointer transition-all duration-fast inline-flex items-center gap-2 hover:bg-primary-500 hover:-translate-y-0.5 hover:shadow-lg active:translate-y-0">
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polygon points="5 3 19 12 5 21 5 3"/>
              </svg>
              Create Your First Run
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="h-full flex flex-col bg-transparent overflow-hidden">
        <div className="page-header px-4 lg:px-6">
          <div className="page-header-content">
            <h2 className="page-title text-lg lg:text-xl">Test & Preview</h2>
            <p className="page-subtitle text-xs lg:text-sm">
              {filteredRuns.length}{searchRun && ` of ${runs.length}`} {filteredRuns.length === 1 ? 'run' : 'runs'}
            </p>
          </div>
          <div className="page-actions">
            <button onClick={handleNewRun} className="btn-primary text-xs lg:text-sm px-3 lg:px-4 py-2">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4">
                <polygon points="5 3 19 12 5 21 5 3"/>
              </svg>
              <span className="hidden sm:inline">New Run</span>
              <span className="sm:hidden">New</span>
            </button>
          </div>
        </div>

        {/* Search Bar */}
        <div className="px-4 lg:px-6 py-3 border-b border-border-primary/50">
          <div className="relative w-full">
            <svg 
              className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-tertiary pointer-events-none" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="2"
            >
              <circle cx="11" cy="11" r="8"/>
              <path d="m21 21-4.35-4.35"/>
            </svg>
            <input
              type="text"
              placeholder="Search by run name..."
              value={searchRun}
              onChange={(e) => setSearchRun(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-bg-secondary border border-border-primary rounded-lg text-sm text-text-primary placeholder:text-text-tertiary focus:outline-none transition-all"
            />
            {searchRun && (
              <button
                onClick={() => setSearchRun('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-text-tertiary hover:text-text-secondary transition-colors"
              >
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </button>
            )}
          </div>
        </div>

        {/* Empty state when search has no results */}
        {filteredRuns.length === 0 && searchRun ? (
          <div className="flex flex-col items-center justify-center min-h-[400px] text-center p-16 max-w-2xl mx-auto">
            <div className="w-16 h-16 rounded-full bg-bg-tertiary flex items-center justify-center mb-4 border border-border-primary">
              <svg className="w-8 h-8 text-text-tertiary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"/>
                <path d="m21 21-4.35-4.35"/>
              </svg>
            </div>
            <h3 className="text-text-primary text-lg font-semibold mb-2">No Matching Runs</h3>
            <p className="text-text-secondary text-sm mb-4">No runs match your search query. Try adjusting your search.</p>
            <button
              onClick={() => setSearchRun('')}
              className="px-4 py-2 text-sm font-medium bg-primary-600 text-white rounded-lg cursor-pointer transition-all inline-flex items-center gap-2 hover:bg-primary-500"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M6 18L18 6M6 6l12 12"/>
              </svg>
              Clear Search
            </button>
          </div>
        ) : (
          <>
        {/* Desktop Table View */}
        <div className="hidden lg:block flex-1 overflow-x-auto overflow-y-auto bg-bg-secondary">
          <table className="w-full border-separate border-spacing-0">
            <thead className="sticky top-0 z-10 bg-bg-tertiary">
              <tr>
                <th className="py-3 px-4 text-left text-xs font-semibold text-text-tertiary uppercase tracking-wider border-b border-border-primary whitespace-nowrap">Run Name</th>
                <th className="py-3 px-4 text-left text-xs font-semibold text-text-tertiary uppercase tracking-wider border-b border-border-primary whitespace-nowrap">Project</th>
                <th className="py-3 px-4 text-left text-xs font-semibold text-text-tertiary uppercase tracking-wider border-b border-border-primary whitespace-nowrap">Latency</th>
                <th className="py-3 px-4 text-left text-xs font-semibold text-text-tertiary uppercase tracking-wider border-b border-border-primary whitespace-nowrap">Model</th>
                <th className="py-3 px-4 text-left text-xs font-semibold text-text-tertiary uppercase tracking-wider border-b border-border-primary whitespace-nowrap">Created</th>
                <th className="py-3 px-4 text-center text-xs font-semibold text-text-tertiary uppercase tracking-wider border-b border-border-primary whitespace-nowrap w-20">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredRuns.map((run) => (
                <tr
                  key={run.id}
                  className="cursor-pointer transition-all duration-fast bg-bg-secondary hover:bg-bg-hover"
                  onClick={() => handleViewRun(run)}
                >
                  <td className="py-3 px-4 border-b border-border-primary text-sm text-text-secondary align-middle w-64">
                    <div className="flex items-center gap-2">
                      <svg className="w-3.5 h-3.5 text-primary-500 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polygon points="5 3 19 12 5 21 5 3"/>
                      </svg>
                      <span className="font-semibold text-text-primary font-mono whitespace-nowrap overflow-hidden text-ellipsis">{run.run_name}</span>
                    </div>
                  </td>
                  <td className="py-3 px-4 border-b border-border-primary text-sm text-text-secondary align-middle">
                    {run.project_name ? (
                      <span className="inline-block py-1 px-2 text-xs font-medium font-mono rounded-md whitespace-nowrap bg-bg-tertiary text-text-secondary border border-border-primary">{run.project_name}</span>
                    ) : (
                      <span className="text-text-tertiary">-</span>
                    )}
                  </td>
                  <td className="py-3 px-4 border-b border-border-primary text-sm text-text-secondary align-middle">
                    <span className="font-mono text-sm">
                      {run.latency_ms ? `${(run.latency_ms / 1000).toFixed(2)}s` : '-'}
                    </span>
                  </td>
                  <td className="py-3 px-4 border-b border-border-primary text-sm text-text-secondary align-middle">
                    <span className="inline-block py-1 px-2 text-xs font-medium font-mono rounded-md whitespace-nowrap bg-primary-600 text-white">{getModelDisplayName(run.model)}</span>
                  </td>
                  <td className="py-3 px-4 border-b border-border-primary text-sm text-text-tertiary align-middle w-44 text-[13px] whitespace-nowrap">
                    {new Date(run.created_at).toLocaleDateString('en-US', { 
                      month: 'short', 
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </td>
                  <td className="py-2 px-4 border-b border-border-primary text-sm text-text-secondary align-middle w-15 text-center">
                    <button
                      className="p-1 bg-transparent border-none rounded-md cursor-pointer transition-all duration-fast inline-flex items-center justify-center hover:bg-bg-hover group"
                      onClick={(e) => handleDeleteRun(run.id, e)}
                      title="Delete run"
                      aria-label="Delete run"
                    >
                      <svg className="w-4 h-4 text-text-tertiary group-hover:text-red-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="3 6 5 6 21 6"></polyline>
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                        <line x1="10" y1="11" x2="10" y2="17"></line>
                        <line x1="14" y1="11" x2="14" y2="17"></line>
                      </svg>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile Card View */}
        <div className="lg:hidden flex-1 overflow-y-auto p-4 space-y-3 bg-bg-secondary">
          {filteredRuns.map((run) => (
            <div
              key={run.id}
              className="bg-bg-primary border border-border-primary rounded-lg p-4 cursor-pointer transition-all duration-fast hover:shadow-md hover:border-primary-300 active:scale-[0.98]"
              onClick={() => handleViewRun(run)}
            >
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <svg className="w-4 h-4 text-primary-500 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polygon points="5 3 19 12 5 21 5 3"/>
                  </svg>
                  <h3 className="text-sm font-semibold text-text-primary font-mono truncate">{run.run_name}</h3>
                </div>
                <button
                  className="p-1.5 bg-transparent border-none rounded-md cursor-pointer transition-all duration-fast inline-flex items-center justify-center hover:bg-error-bg group flex-shrink-0"
                  onClick={(e) => handleDeleteRun(run.id, e)}
                  title="Delete run"
                  aria-label="Delete run"
                >
                  <svg className="w-4 h-4 text-text-tertiary group-hover:text-error" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="3 6 5 6 21 6"></polyline>
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                    <line x1="10" y1="11" x2="10" y2="17"></line>
                    <line x1="14" y1="11" x2="14" y2="17"></line>
                  </svg>
                </button>
              </div>

              {run.project_name && (
                <div className="mb-3">
                  <span className="text-xs text-text-tertiary">Project: </span>
                  <span className="inline-block py-1 px-2 text-xs font-medium font-mono rounded-md whitespace-nowrap bg-bg-tertiary text-text-secondary border border-border-primary">
                    {run.project_name}
                  </span>
                </div>
              )}

              {run.latency_ms && (
                <div className="mb-3">
                  <span className="text-xs text-text-tertiary">Latency: </span>
                  <span className="font-mono text-sm font-semibold text-text-primary">
                    {(run.latency_ms / 1000).toFixed(2)}s
                  </span>
                </div>
              )}

              <div className="flex flex-wrap gap-2 mb-3">
                <span className="inline-block py-1 px-2 text-xs font-medium font-mono rounded-md whitespace-nowrap bg-primary-600 text-white">
                  {getModelDisplayName(run.model)}
                </span>
              </div>

              <div className="flex items-center gap-2 text-xs text-text-tertiary">
                <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <polyline points="12 6 12 12 16 14"/>
                </svg>
                <span>
                  {new Date(run.created_at).toLocaleDateString('en-US', { 
                    month: 'short', 
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </span>
              </div>
            </div>
          ))}
        </div>
          </>
        )}
      </div>
    );
  }

  if (viewMode === 'wizard') {
    const filteredProjects = completedProjects.filter(p => 
      p.project_name?.toLowerCase().includes(searchProject.toLowerCase())
    );
    
    const WIZARD_STEPS = [
      { id: 'project', title: 'Select Prompt' },
      { id: 'configure', title: 'Configure' },
      { id: 'inputs', title: 'Fill Inputs' },
    ];
    
    return (
      <div className="w-full h-screen flex justify-center items-center py-6 px-6 bg-bg-primary overflow-hidden">
        <div className="w-full max-w-[850px] h-[calc(100vh-48px)] max-h-[900px] bg-bg-secondary rounded-xl border border-border-primary shadow-2xl overflow-hidden flex flex-col animate-fadeIn">
          {selectedProject && (
            <div className="flex items-center gap-2 py-4 px-6 bg-gradient-to-b from-bg-tertiary to-bg-secondary border-b border-border-primary flex-shrink-0">
              <span className="text-[10px] font-semibold text-text-tertiary uppercase tracking-wider">Project:</span>
              <span className="text-base font-semibold text-text-primary font-mono">{selectedProject.project_name}</span>
            </div>
          )}
          
          <div className="py-5 px-6 bg-bg-tertiary border-b border-border-primary flex-shrink-0">
            <div className="flex justify-between mb-4 relative">
              {WIZARD_STEPS.map((step, index) => (
                <div
                  key={step.id}
                  className="flex flex-col items-center gap-2 flex-1 relative"
                >
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-semibold border-2 transition-all duration-300 shadow-sm z-[2] ${index + 1 === wizardStep ? 'bg-primary-600 text-white border-primary-600 ring-4 ring-primary-500/10 scale-105' : index + 1 < wizardStep ? 'bg-primary-600 text-white border-primary-600' : 'bg-bg-secondary text-text-tertiary border-border-primary'}`}>
                    {index + 1 < wizardStep ? (
                      <span className="text-sm">✓</span>
                    ) : (
                      <span className="text-[13px]">{index + 1}</span>
                    )}
                  </div>
                  <span className={`text-xs font-medium text-center transition-all duration-fast whitespace-nowrap ${index + 1 === wizardStep ? 'text-text-primary font-semibold' : index + 1 < wizardStep ? 'text-text-secondary' : 'text-text-tertiary'}`}>{step.title}</span>
                </div>
              ))}
            </div>
            <div className="h-[3px] bg-border-primary rounded-full overflow-hidden relative">
              <div 
                className="h-full bg-gradient-to-r from-primary-600 to-primary-500 rounded-full transition-[width] duration-[400ms] ease-smooth relative shadow-[0_0_8px_rgba(99,102,241,0.3)]" 
                style={{ width: `${((wizardStep - 1) / 2) * 100}%` }}
              />
            </div>
          </div>

          <div className="flex-1 flex flex-col min-h-0 overflow-hidden p-0 m-0">
            <div className="flex-1 py-6 px-6 overflow-y-auto overflow-x-hidden min-h-0 [&::-webkit-scrollbar]:w-[10px] [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-border-primary [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb:hover]:bg-text-tertiary">
            {wizardStep === 1 && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <h3 className="text-lg font-semibold text-text-primary">Select a Prompt</h3>
                  <p className="text-sm text-text-secondary">Choose which prompt you want to run</p>
                </div>
                
                <input
                  type="text"
                  className="w-full px-4 py-2.5 text-sm text-text-primary bg-bg-elevated border border-primary-700/50 rounded-lg focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 transition-all duration-fast placeholder:text-text-tertiary shadow-inner"
                  placeholder="Search projects..."
                  value={searchProject}
                  onChange={(e) => setSearchProject(e.target.value)}
                />

                {filteredProjects.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 px-6 text-center bg-bg-tertiary border border-border-primary rounded-lg">
                    <p className="text-sm text-text-secondary">{searchProject ? 'No prompts match your search' : 'No completed prompts available'}</p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-[400px] overflow-y-auto pr-2 [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-border-primary [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb:hover]:bg-text-tertiary">
                    {filteredProjects.map((proj) => (
                      <div
                        key={proj.id}
                        className={`relative p-3 bg-bg-secondary border-2 rounded-lg cursor-pointer transition-all duration-fast hover:border-primary-500 hover:z-10 hover:shadow-lg ${selectedProject?.id === proj.id ? 'border-primary-500 bg-primary-500/5 ring-2 ring-primary-500/20 z-10' : 'border-border-primary'}`}
                        onClick={() => handleSelectProjectFromList(proj)}
                      >
                        <div className="flex items-center justify-between gap-3">
                          <span className="text-sm font-semibold text-text-primary truncate">{proj.project_name || 'Untitled'}</span>
                          <div className="flex items-center gap-1.5 flex-shrink-0">
                            <span className="text-[10px] px-1.5 py-0.5 rounded font-mono bg-bg-tertiary text-text-secondary border border-border-primary">
                              {proj.user_input?.type || 'N/A'}
                            </span>
                            <span className="text-[10px] px-1.5 py-0.5 rounded font-mono bg-primary-600 text-white">
                              {proj.user_input?.output_type || 'Text'}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {wizardStep === 2 && selectedProject && (
              <div className="space-y-6">
                <div className="space-y-2">
                  <h3 className="text-lg font-semibold text-text-primary">Run Configuration</h3>
                  <p className="text-sm text-text-secondary">Set up run details for this prompt execution</p>
                </div>

                <div className="space-y-2.5">
                  <label className="block text-sm font-medium text-text-primary">
                    Run Name <span className="text-error-500">*</span>
                  </label>
                  <input
                    type="text"
                    className="w-full px-4 py-2.5 text-sm text-text-primary bg-bg-elevated border border-primary-700/50 rounded-lg focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 transition-all duration-fast placeholder:text-text-tertiary shadow-inner"
                    placeholder="Enter a name for this run..."
                    value={runName}
                    onChange={(e) => setRunName(e.target.value)}
                    autoFocus
                  />
                </div>

                <div className="space-y-2.5">
                  <label className="block text-sm font-medium text-text-primary">Model Configuration</label>
                  <div className="text-xs text-text-secondary mb-1">
                    {selectedModel ? (
                      <>Currently using: <span className="font-mono font-medium text-text-primary">{getModelDisplayName(selectedModel)}</span></>
                    ) : (
                      <>Using project default: <span className="font-mono font-medium text-text-primary">{getModelDisplayName(selectedProject.model || DEFAULT_MODEL)}</span></>
                    )}
                  </div>
                  <ModelSelector
                    model={selectedModel || selectedProject.model || DEFAULT_MODEL}
                    setModel={setSelectedModel}
                    llmConfig={llmConfig}
                    setLLMConfig={setLLMConfig}
                    availableProviders={availableProviders}
                  />
                  <p className="text-xs text-text-tertiary mt-2">
                    Click the settings icon above to change the model or adjust parameters
                  </p>
                </div>
              </div>
            )}

            {wizardStep === 3 && selectedProject && (
              <div className="space-y-6">
                <div className="space-y-2">
                  <h3 className="text-lg font-semibold text-text-primary">Input Variables</h3>
                  <p className="text-sm text-text-secondary">Provide values for all required variables</p>
                </div>

                {userVariables.length > 0 && (
                  <div className="space-y-4">
                    <h4 className="text-sm font-semibold text-text-primary border-b border-border-primary pb-2">Required Variables</h4>
                    {userVariables.map(variable => renderVariableInput(variable, true))}
                  </div>
                )}

                {additionalVariables.length > 0 && (
                  <div className="space-y-4">
                    <h4 className="text-sm font-semibold text-text-primary border-b border-border-primary pb-2">Optional Variables</h4>
                    {additionalVariables.map(variable => renderVariableInput(variable, false))}
                  </div>
                )}
              </div>
            )}
            </div>
            </div>

          <div className="flex justify-between items-center py-4 px-6 border-t border-border-primary bg-bg-secondary flex-shrink-0">
              {wizardStep > 1 ? (
                <button
                  type="button"
                  onClick={handlePrevStep}
                className="py-2 px-4 text-sm font-medium bg-bg-tertiary text-text-primary border border-border-primary rounded-md cursor-pointer transition-all duration-fast inline-flex items-center justify-center gap-2 whitespace-nowrap hover:bg-bg-hover hover:border-border-hover disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Back
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleBackToAllRuns}
                className="py-2 px-4 text-sm font-medium bg-bg-tertiary text-text-primary border border-border-primary rounded-md cursor-pointer transition-all duration-fast inline-flex items-center justify-center gap-2 whitespace-nowrap hover:bg-bg-hover hover:border-border-hover disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
              )}
              {wizardStep < 3 ? (
                <button
                  type="button"
                  onClick={handleNextStep}
                className="py-2 px-4 text-sm font-medium bg-primary-600 text-white border border-primary-600 rounded-md cursor-pointer transition-all duration-fast inline-flex items-center justify-center gap-2 whitespace-nowrap hover:bg-primary-500 hover:border-primary-500 hover:-translate-y-px hover:shadow-md active:bg-primary-700 active:border-primary-700 active:translate-y-0 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={wizardStep === 1 && !selectedProject}
                >
                  Continue
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleRun}
                className="py-2 px-4 text-sm font-medium bg-primary-600 text-white border border-primary-600 rounded-md cursor-pointer transition-all duration-fast inline-flex items-center justify-center gap-2 whitespace-nowrap hover:bg-primary-500 hover:border-primary-500 hover:-translate-y-px hover:shadow-md active:bg-primary-700 active:border-primary-700 active:translate-y-0 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={isRunning || !runName.trim()}
                >
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polygon points="5 3 19 12 5 21 5 3"/>
                  </svg>
                  {isRunning ? 'Running...' : 'Run Prompt'}
                </button>
              )}
          </div>
        </div>
      </div>
    );
  }

  if (viewMode === 'view' && selectedRun) {
    return (
      <>
        <div className="h-full flex flex-col bg-bg-primary overflow-hidden">
        {/* Header - Uses same classes as ProjectDetail */}
        <div className="pd-header">
            <button onClick={handleBackToAllRuns} className="btn-back" disabled={isRunning}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="19" y1="12" x2="5" y2="12"></line>
              <polyline points="12 19 5 12 12 5"></polyline>
            </svg>
            Back to All Runs
            </button>
          
          <div className="pd-header-content">
            <div className="pd-title-row">
              <h2 className="pd-title">{selectedRun.run_name || 'Untitled Run'}</h2>
              {isRunning && <span className="streaming-badge">Running...</span>}
            </div>
            {!isRunning && (
              <div className="pd-metadata">
                {selectedRun.created_at && (
                  <span className="pd-meta-item">
                    <span className="pd-meta-label">Created:</span>
                    <span className="pd-meta-value">{new Date(selectedRun.created_at).toLocaleString()}</span>
                  </span>
                )}
                {selectedRun.commit_id && (
                  <span className="pd-meta-item">
                    <span className="pd-meta-label">Version:</span>
                    <code className="pd-commit-id">{selectedRun.commit_id}</code>
                  </span>
                )}
              </div>
            )}
          </div>

          {!isRunning && (
            <div className="pd-actions">
              <button onClick={() => handleRunAgain(selectedRun)} className="btn-action-primary">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="1 4 1 10 7 10"></polyline>
                  <polyline points="23 20 23 14 17 14"></polyline>
                  <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15"></path>
                </svg>
                Run Again
              </button>
            </div>
          )}
        </div>

        {/* Tabs - Uses same classes as ProjectDetail */}
        <div className="pd-tabs">
              <button
            className={`pd-tab ${activeResultTab === 'input' ? 'active' : ''}`}
                onClick={() => setActiveResultTab('input')}
              >
            Input Values
              </button>
              <button
            className={`pd-tab ${activeResultTab === 'output' ? 'active' : ''}`}
                onClick={() => setActiveResultTab('output')}
              >
                Output
              </button>
                <button
            className={`pd-tab ${activeResultTab === 'prompts' ? 'active' : ''}`}
            onClick={() => setActiveResultTab('prompts')}
                >
            Prompts Used
                </button>
            </div>

        {/* Content - Uses same classes as ProjectDetail */}
        <div className="pd-content">
          {activeResultTab === 'input' && (
            <div className="pd-input-section">
              <div className="pd-grid">
                <div className="pd-card">
                  <h3 className="pd-section-title">Prompt</h3>
                  <div className="pd-card-value">{selectedRun.project_name || 'N/A'}</div>
                </div>
                <div className="pd-card">
                  <h3 className="pd-section-title">Model</h3>
                  <div className="pd-card-value">{getModelDisplayName(selectedRun.model)}</div>
                </div>
                <div className="pd-card">
                  <h3 className="pd-section-title">Created</h3>
                  <div className="pd-card-value">{new Date(selectedRun.created_at).toLocaleDateString()}</div>
                </div>
                {selectedRun.commit_id && (
                  <div className="pd-card">
                    <h3 className="pd-section-title">Version</h3>
                    <div className="pd-card-value">
                      <code className="pd-commit-id">{selectedRun.commit_id}</code>
                    </div>
                  </div>
                )}
              </div>
              
              {selectedRun.input_values && Object.keys(selectedRun.input_values).length > 0 && (
                <div className="pd-card">
                  <h3 className="pd-section-title">Input Variables</h3>
                  <div className="pd-variables">
                    {Object.entries(selectedRun.input_values).map(([key, value]) => (
                      <div key={key} className="pd-variable-row">
                        <span className="pd-variable-name">{key}</span>
                        <span className="pd-variable-desc">
                          {Array.isArray(value) ? value.join(', ') : value}
                        </span>
                        <span className="pd-badge">{Array.isArray(value) ? 'Array' : 'String'}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {activeResultTab === 'output' && (
            <div className="prompts-view">
              <div className="output-tabs-container">
                <div className="output-tabs">
                  <span className="output-tab active">Generated Output</span>
                </div>
                <button
                  onClick={() => handleCopy(selectedRun.output || output || '')}
                  className="btn-copy"
                  disabled={isRunning}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                  </svg>
                  Copy
                </button>
              </div>
              <div className="output-panel">
                <pre ref={outputRef} className="panel-content md-highlighted">
                  {isRunning ? (
                    <>
                      {output ? highlightMarkdown(output) : 'Generating...'}
                      {output && '▌'}
                    </>
                  ) : selectedRun.output ? (
                    highlightMarkdown(selectedRun.output)
                  ) : (
                    output ? highlightMarkdown(output) : 'No output available'
                  )}
                </pre>
              </div>
            </div>
          )}

          {activeResultTab === 'prompts' && (
            <div className="prompts-view">
              <div className="output-tabs-container">
                <div className="output-tabs">
                  {(selectedRun.system_prompt || result?.system_prompt) && (
                    <button
                      className={`output-tab ${activePromptType === 'system' ? 'active' : ''}`}
                      onClick={() => setActivePromptType('system')}
                    >
                      System Prompt
                    </button>
                  )}
                  {(selectedRun.user_prompt || result?.user_prompt) && (
                    <button
                      className={`output-tab ${activePromptType === 'user' ? 'active' : ''}`}
                      onClick={() => setActivePromptType('user')}
                    >
                      User Prompt
                    </button>
                  )}
                </div>
                <button
                  onClick={() => {
                    const text = activePromptType === 'system' 
                      ? (selectedRun.system_prompt || result?.system_prompt)
                      : (selectedRun.user_prompt || result?.user_prompt);
                    handleCopy(text || '');
                  }}
                  className="btn-copy"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                  </svg>
                  Copy
                </button>
              </div>
              <div className="output-panel">
                <pre className="panel-content md-highlighted">
                  {activePromptType === 'system' 
                    ? highlightMarkdown(selectedRun.system_prompt || result?.system_prompt)
                    : highlightMarkdown(selectedRun.user_prompt || result?.user_prompt)
                  }
                </pre>
              </div>
            </div>
          )}
        </div>

      </div>
      </>
    );
  }

  return null;
};

TestPromptPage.propTypes = {
  project: PropTypes.object,
  onBack: PropTypes.func,
  onSelectProject: PropTypes.func,
  projects: PropTypes.array,
  runs: PropTypes.array,
  runsLoading: PropTypes.bool,
  onRunsUpdate: PropTypes.func.isRequired
};

TestPromptPage.defaultProps = {
  project: null,
  onBack: null,
  onSelectProject: null,
  projects: [],
};

export default TestPromptPage;
